import React, { useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import Modal from "~components/Modal";

import "./style.scss";
import Select from "~components/Form/components/Select";
import InputField from "~components/Form/components/InputField";
import { Form, Formik } from "formik";
import { Textarea } from "@chakra-ui/react";
import usePrazoPagamento from "~/fetchs/usePrazoPagamento";
import useModalInformacoesAdicionais from "./useModalInformacoesAdicionais";
import useMotivo from "~/fetchs/useMotivo";

const InformacoesAdicionaisModal = ({ isOpen, onClose, handleSubmit, submitLabel }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      sub={<Content handleSubmit={handleSubmit} submitLabel={submitLabel} />}
      hideButtonTwo={true}
      hideButtonOne={true}
    />
  );
};

InformacoesAdicionaisModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  submitLabel: PropTypes.string,
};

const Content = ({ handleSubmit, submitLabel }) => {
  const formikRef = useRef();
  const { initial, validation } = useModalInformacoesAdicionais();

  const { onChangePrazoPagamento, prazoPagamentoSelect } = usePrazoPagamento(formikRef, "prazoVigenciaId");
  const { optionMotivos, onChangeMotivo, motivoSelect } = useMotivo({ formikRef: formikRef });

  const calculateDates = useCallback((value) => {
    const startDate = new Date();
    const finalDate = new Date();

    if (value !== "personalizado") {
      finalDate.setDate(finalDate.getDate() + (value.days || 0));
      finalDate.setMonth(finalDate.getMonth() + (value.months || 0));
      finalDate.setFullYear(finalDate.getFullYear() + (value.years || 0));
    }

    const startDateString = [
      startDate.getFullYear(),
      (startDate.getMonth() + 1).toString().padStart(2, "0"),
      startDate.getDate().toString().padStart(2, "0"),
    ].join("-");

    const finalDateString =
      value === "personalizado"
        ? ""
        : [
            finalDate.getFullYear(),
            (finalDate.getMonth() + 1).toString().padStart(2, "0"),
            finalDate.getDate().toString().padStart(2, "0"),
          ].join("-");

    return { startDateString, finalDateString };
  }, []);

  const handleChangePrazo = useCallback(
    (e) => {
      onChangePrazoPagamento(e);

      const { startDateString, finalDateString } = calculateDates(e.value);

      if (formikRef.current) {
        formikRef.current.setValues((prev) => ({
          ...prev,
          prazoVigenciaId: e.value,
          inicioVigencia: startDateString,
          fimVigencia: finalDateString,
        }));
      }
    },
    [onChangePrazoPagamento, calculateDates],
  );

  const handleChangeDates = useCallback(() => {
    onChangePrazoPagamento({ label: "Personalizado", value: "personalizado" });

    if (formikRef.current) {
      formikRef.current.setFieldValue("prazoVigenciaId", "personalizado");
    }
  }, [onChangePrazoPagamento]);

  const optionPrazosPagamento = useMemo(
    () => [
      { label: "1 Mês", value: { months: 1 } },
      { label: "3 Meses", value: { months: 3 } },
      { label: "6 Meses", value: { months: 6 } },
      { label: "1 Ano", value: { years: 1 } },
      { label: "Personalizado", value: "personalizado" },
    ],
    [],
  );

  const maxlenght = 200;

  const handleJustificativaChange = useCallback((e) => {
    const value = e.target.value;
    if (formikRef.current) {
      formikRef.current.setFieldValue("justificativa", value);
    }
  }, []);

  return (
    <main className="prices-table-modal-informacoes-adicionais">
      <h1 className="title">Informações Adicionais:</h1>
      <Formik
        initialValues={initial}
        onSubmit={handleSubmit}
        validationSchema={validation}
        innerRef={formikRef}
        enableReinitialize={true}
        data-select-id="form-informacoes-adicionais"
      >
        {({ values, errors, touched }) => (
          <Form className="form">
            <Select
              label={<>Informe o prazo de vigência das negociações solicitadas:</>}
              name="prazoVigenciaId"
              options={optionPrazosPagamento}
              onChange={handleChangePrazo}
              errors={errors.prazoVigenciaId}
              touched={touched.prazoVigenciaId}
              isRequired={true}
              data-test-id="select-prazo"
              {...prazoPagamentoSelect}
            />
            <div className="flex gap-x-4">
              <InputField
                labelName="Início:"
                inputName="inicioVigencia"
                inputMask="offmask"
                inputType="date"
                isRequired={true}
                isDisabled={false}
                onChangeEvent={handleChangeDates}
                errors={errors.inicioVigencia}
                touched={touched.inicioVigencia}
                data-test-id="input-inicio-vigencia"
              />
              <InputField
                labelName="Fim:"
                inputName="fimVigencia"
                inputMask="offmask"
                inputType="date"
                isRequired={true}
                isDisabled={false}
                onChangeEvent={handleChangeDates}
                errors={errors.fimVigencia}
                touched={touched.fimVigencia}
                data-test-id="input-fim-vigencia"
              />
            </div>
            <Select
              name="motivoId"
              label={<>Informe o Motivo das negociações solicitadas:</>}
              options={optionMotivos}
              errors={errors.motivoId}
              touched={touched.motivoId}
              onChange={(e) => onChangeMotivo(e)}
              isRequired={true}
              data-test-id="select-motivo-negociacoes"
              {...motivoSelect}
            />
            <label className="label">
              <p className="desc">
                Informe a Justificativa das negociações solicitadas: <span style={{ color: "#e53e3e" }}>*</span>
              </p>
              <Textarea
                name="justificativa"
                placeholder="Digite sua descrição aqui..."
                maxLength={maxlenght}
                value={values.justificativa || ""}
                onChange={handleJustificativaChange}
                onBlur={() => formikRef.current.setFieldTouched("justificativa", true)}
                className={errors.justificativa && touched.justificativa ? "error" : ""}
                style={{
                  borderColor: errors.justificativa && touched.justificativa ? "#e53e3e" : "",
                  borderWidth: errors.justificativa && touched.justificativa ? "2px" : "1px",
                }}
                data-test-id="textarea-justificativa"
              />
              {errors.justificativa && touched.justificativa && (
                <p className="error-message" style={{ color: "#e53e3e", fontSize: "14px", marginTop: "4px" }}>
                  {errors.justificativa}
                </p>
              )}
              <div className="flex justify-end w-full">
                <p className="opacity-40 px-1 desc">
                  {(values.justificativa || "").length}/{maxlenght}
                </p>
              </div>
            </label>
            <button
              type="button"
              className="submit-button"
              onClick={() => formikRef.current.submitForm()}
              data-test-id="button-additional-information-submit"
            >
              {submitLabel ?? "Enviar Negociação"}
            </button>
          </Form>
        )}
      </Formik>
    </main>
  );
};

Content.propTypes = {
  handleSubmit: PropTypes.func,
  submitLabel: PropTypes.string,
};

export default InformacoesAdicionaisModal;
